import {
  AiOnboardingCatalogs,
  OnboardingProfileSection,
  OnboardingProfileSectionFields,
  OnboardingRepository,
  OnboardingSnapshot,
  SoftwareExperiencePercentage,
  SoftwareExperiencePercentageInput,
  TrustedOnboardingProfileFields,
  ValidateAiOnboardingProfile,
} from './types';
import {
  formatValidationErrors,
  onboardingProfileSectionFieldNames,
  onboardingProfileSectionSchemas,
  validateClientToolParameters,
} from './validation';
import { normalizeOnboardingCatalogSectionFields } from './catalog';
import {
  normalizeAiOnboardingPhoneNumber,
  normalizeAiOnboardingPostalCode,
} from './onboardingMappers';
import {
  isRecord,
  normalizeNestedRemainingSoftwarePercentage,
} from './toolParameters';

interface IProfileSectionToolDependencies<TCompleteProfileResult> {
  getRepository: () => OnboardingRepository;
  getTrustedProfileFields: () => TrustedOnboardingProfileFields;
  saveCompleteProfile: (parameters: unknown) => Promise<TCompleteProfileResult>;
  validateProfile: ValidateAiOnboardingProfile;
}

interface IProfileSectionParseResult {
  fields: OnboardingProfileSectionFields;
  validationErrors: string[];
}

// The last section completes the profile, so saving it runs the same checks as
// the single full-profile save.
const finalOnboardingProfileSection = OnboardingProfileSection.Availability;

// The signup-form validators name the capacity differently, and the phone they
// check is derived from the mobile number collected with the contact details.
const manualValidationFieldAliases: Partial<
Record<keyof OnboardingProfileSectionFields, string[]>
> = {
  mobile: ['phone'],
  professionalCapacityId: ['pharmacistCapacityId'],
};

const createValidationFailure = (validationErrors: string[]) =>
  ({
    success: false,
    errorCode: 'VALIDATION_ERROR',
    validationErrors,
  });

const createLocalStateFailure = () =>
  ({
    success: false,
    errorCode: 'LOCAL_STATE_ERROR',
  });

const includeTrustedRole = (
  parameters: unknown,
  role: TrustedOnboardingProfileFields['role'],
) => {
  if (!isRecord(parameters)) {
    return parameters;
  }

  const { fields } = parameters;

  if (!isRecord(fields)) {
    return parameters;
  }

  return {
    ...parameters,
    fields: {
      ...fields,
      role,
    },
  };
};

const toSoftwareExperiencePercentage = (
  percentage?: SoftwareExperiencePercentageInput,
) => {
  if (!percentage) {
    return null;
  }

  return Number(percentage) as SoftwareExperiencePercentage;
};

const normalizeSectionContactFields = (
  fields: OnboardingProfileSectionFields,
): OnboardingProfileSectionFields => {
  const { mobile, postalCode } = fields;

  return {
    ...fields,
    ...(typeof mobile === 'string' && {
      mobile: normalizeAiOnboardingPhoneNumber(mobile),
    }),
    ...(typeof postalCode === 'string' && {
      postalCode: normalizeAiOnboardingPostalCode(postalCode),
    }),
  };
};

const normalizeSectionFields = (
  fields: OnboardingProfileSectionFields,
  catalogs: AiOnboardingCatalogs | null,
  remainingSoftwarePercentage: SoftwareExperiencePercentage | null,
): IProfileSectionParseResult => {
  const catalogNormalization = catalogs
    ? normalizeOnboardingCatalogSectionFields(
      fields,
      catalogs,
      remainingSoftwarePercentage,
    )
    : { fields, validationErrors: [] };

  return {
    fields: normalizeSectionContactFields(catalogNormalization.fields),
    validationErrors: catalogNormalization.validationErrors,
  };
};

const getSectionValidationFieldNames = (
  sectionName: OnboardingProfileSection,
) =>
  new Set(onboardingProfileSectionFieldNames[sectionName].flatMap((fieldName) =>
    [fieldName, ...(manualValidationFieldAliases[fieldName] ?? [])]));

const getValidationErrorFieldName = (validationError: string) =>
  validationError.split(/[.:[]/)[0].trim();

const createManualValidationProfile = (
  profileDraft: OnboardingProfileSectionFields,
): OnboardingProfileSectionFields => {
  const { mobile } = profileDraft;

  return {
    ...profileDraft,
    ...(typeof mobile === 'string' && { phone: mobile }),
  };
};

export const createProfileSectionClientTools = <TCompleteProfileResult>({
  getRepository,
  getTrustedProfileFields,
  saveCompleteProfile,
  validateProfile,
}: IProfileSectionToolDependencies<TCompleteProfileResult>) => {
  const parseProfileSection = async (
    sectionName: OnboardingProfileSection,
    parameters: unknown,
  ): Promise<IProfileSectionParseResult> => {
    const { catalogs, role } = getTrustedProfileFields();
    const validation = await validateClientToolParameters(
      onboardingProfileSectionSchemas[sectionName],
      includeTrustedRole(
        normalizeNestedRemainingSoftwarePercentage(parameters),
        role,
      ),
    );

    if (!validation.success) {
      return {
        fields: {},
        validationErrors: formatValidationErrors(validation.error),
      };
    }

    return normalizeSectionFields(
      validation.data.fields,
      catalogs ?? null,
      toSoftwareExperiencePercentage(validation.data.remainingSoftwarePercentage),
    );
  };

  // The rest of the profile is not collected yet, so only the manual-validation
  // errors about this section's own fields are reported now. The final section
  // reports every remaining error through the full-profile save.
  const getSectionValidationErrors = async (
    sectionName: OnboardingProfileSection,
    profileDraft: OnboardingProfileSectionFields,
  ) => {
    const validationErrors = await validateProfile(
      createManualValidationProfile(profileDraft),
    );
    const sectionFieldNames = getSectionValidationFieldNames(sectionName);

    return validationErrors.filter((validationError) =>
      sectionFieldNames.has(getValidationErrorFieldName(validationError)));
  };

  const saveSectionDraft = async (
    sectionName: OnboardingProfileSection,
    sectionFields: OnboardingProfileSectionFields,
    profileDraft: OnboardingProfileSectionFields,
  ) => {
    let validationErrors: string[];

    try {
      validationErrors = await getSectionValidationErrors(
        sectionName,
        profileDraft,
      );
    } catch {
      return createValidationFailure([
        'input: Unable to validate the supplied details',
      ]);
    }

    if (validationErrors.length > 0) {
      return createValidationFailure(validationErrors);
    }

    try {
      await getRepository().saveOnboardingDetails(sectionFields);
    } catch {
      return createLocalStateFailure();
    }

    return {
      success: true,
    };
  };

  const saveProfileSection = async (
    sectionName: OnboardingProfileSection,
    parameters: unknown,
  ) => {
    const section = await parseProfileSection(sectionName, parameters);

    if (section.validationErrors.length > 0) {
      return createValidationFailure(section.validationErrors);
    }

    let snapshot: OnboardingSnapshot;

    try {
      snapshot = await getRepository().getSnapshot();
    } catch {
      return createLocalStateFailure();
    }

    const {
      firstName,
      lastName,
      preferdLanguage,
      role,
    } = getTrustedProfileFields();
    const profileDraft: OnboardingProfileSectionFields = {
      ...snapshot.onboardingDetails,
      ...section.fields,
      firstName,
      lastName,
      preferdLanguage,
      role,
    };

    if (sectionName === finalOnboardingProfileSection) {
      return saveCompleteProfile({
        fields: profileDraft,
        sectionName: 'onboardingDetails',
      });
    }

    return saveSectionDraft(sectionName, section.fields, profileDraft);
  };

  return {
    save_profile_contact_and_experience: (parameters: unknown) =>
      saveProfileSection(
        OnboardingProfileSection.ContactAndExperience,
        parameters,
      ),
    save_profile_qualifications: (parameters: unknown) =>
      saveProfileSection(OnboardingProfileSection.Qualifications, parameters),
    save_profile_availability: (parameters: unknown) =>
      saveProfileSection(OnboardingProfileSection.Availability, parameters),
  };
};
